import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { Button } from './ui/button';
import { Percent, Wallet, CheckCircle2, Undo2, Smartphone, Copy } from 'lucide-react';

const Stat = ({ label, value, tone, testid }) => (
  <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div data-testid={testid} className={`font-mono-num text-xl font-extrabold ${tone}`}>{value}</div>
  </div>
);

export const CommissionPanel = ({ admin = false }) => {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState('');
  const [payNumber, setPayNumber] = useState('');
  const [payLink, setPayLink] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get(admin ? '/admin/commissions' : '/commissions/mine');
      setData(data); setRate(String(Math.round(data.rate * 100)));
      setPayNumber(data.pay_number || ''); setPayLink(data.pay_link || '');
    } catch {}
  };
  useEffect(() => { load(); }, [admin]);

  const savePayment = async () => {
    try { await api.post('/admin/settings/commission-payment', { pay_number: payNumber, pay_link: payLink }); toast.success('Coordonnées enregistrées'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const saveRate = async () => {
    try { await api.post('/admin/settings/commission', { rate: Number(rate) / 100 }); toast.success('Taux mis à jour'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const settle = async (id, action) => {
    try { await api.post(`/admin/commissions/${id}/${action}`); toast.success('OK'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (!data) return null;
  return (
    <section className="card-elev rounded-2xl p-5" data-testid="commission-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Wallet className="h-5 w-5 text-rose-400" />Commissions</h2>
        {admin ? (
          <div className="flex items-center gap-2 text-sm">
            <Percent className="h-4 w-4 text-slate-400" />
            <input data-testid="commission-rate-input" type="number" min="0" max="50" value={rate} onChange={(e) => setRate(e.target.value)}
              className="w-20 px-2 py-1 rounded-lg bg-slate-900 border border-slate-700 text-white outline-none font-mono-num" />
            <span className="text-slate-400">%</span>
            <Button size="sm" data-testid="commission-rate-save" onClick={saveRate} className="btn-brand text-white">Enregistrer</Button>
          </div>
        ) : (
          <span data-testid="commission-rate" className="text-sm px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-semibold">
            Commission plateforme : {Math.round(data.rate * 100)} % du prix final
          </span>
        )}
      </div>
      <div className={`grid gap-3 mb-4 ${admin ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
        {!admin && <Stat label="Prévue (paiement en attente)" value={fcfa(data.expected)} tone="text-sky-400" testid="commission-expected" />}
        <Stat label="À régler" value={fcfa(data.pending)} tone="text-amber-400" testid="commission-pending" />
        <Stat label="Réglées" value={fcfa(data.settled)} tone="text-emerald-400" testid="commission-settled" />
        <Stat label="Ventes en attente" value={data.count_pending} tone="text-white" testid="commission-count" />
      </div>
      {!admin && data.pending > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="commission-notice">
          <span>Réglez <strong className="font-mono-num">{fcfa(data.pending)}</strong> par Wave / Orange Money au <span className="font-mono-num">{data.pay_number}</span>. Rappel automatique après 7 jours.</span>
          <div className="flex gap-2">
            {data.pay_link ? (
              <a data-testid="pay-commission-wave" href={`${data.pay_link}${data.pay_link.includes('?') ? '&' : '?'}amount=${Math.round(data.pending)}`} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-xs inline-flex items-center gap-1"><Smartphone className="h-3 w-3" />Payer par Wave</a>
            ) : (
              <button data-testid="copy-commission-number" onClick={() => { navigator.clipboard.writeText(data.pay_number); toast.success('Numéro copié'); }}
                className="px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-xs inline-flex items-center gap-1"><Copy className="h-3 w-3" />Copier le numéro</button>
            )}
          </div>
        </div>
      )}
      {admin && (
        <div className="mb-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2 text-sm" data-testid="commission-payment-settings">
          <input data-testid="pay-number-input" value={payNumber} onChange={(e) => setPayNumber(e.target.value)} placeholder="Numéro Wave/OM pour recevoir les commissions"
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none font-mono-num" />
          <input data-testid="pay-link-input" value={payLink} onChange={(e) => setPayLink(e.target.value)} placeholder="Lien Wave Business (https://pay.wave.com/m/…) — optionnel"
            className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none" />
          <Button size="sm" data-testid="pay-settings-save" onClick={savePayment} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold">Enregistrer</Button>
        </div>
      )}
      <div className="space-y-2">
        {data.orders.length === 0 && <div className="text-slate-500 text-sm">Aucune vente pour l'instant.</div>}
        {data.orders.map((o) => (
          <div key={o.order_id} data-testid={`commission-${o.order_id}`}
            className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <div className="font-bold truncate">{o.product_name}</div>
              <div className="text-xs text-slate-400">
                {admin ? `${o.seller_name} · ` : ''}{fcfa(o.amount)} → commission <span className="text-amber-400 font-mono-num">{fcfa(o.commission_amount)}</span>
                {' '}· net vendeur <span className="text-emerald-400 font-mono-num">{fcfa(o.seller_net)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {['won', 'payment_submitted'].includes(o.status) ? (
                <span data-testid={`commission-status-${o.order_id}`} className="text-xs px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300">Prévue · en attente du paiement acheteur</span>
              ) : (
                <span data-testid={`commission-status-${o.order_id}`} className={`text-xs px-2 py-0.5 rounded-full ${o.commission_status === 'collected' ? 'bg-sky-500/20 text-sky-300' : o.commission_status === 'settled' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                  {o.commission_status === 'collected' ? 'Prélevée automatiquement' : o.commission_status === 'settled' ? 'Réglée' : 'À régler'}
                </span>
              )}
              {admin && o.commission_status !== 'settled' && (
                <Button size="sm" data-testid={`settle-${o.order_id}`} onClick={() => settle(o.order_id, 'settle')} className="bg-emerald-600 text-white">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Marquer réglée
                </Button>
              )}
              {admin && o.commission_status === 'settled' && (
                <Button size="sm" variant="outline" data-testid={`unsettle-${o.order_id}`} onClick={() => settle(o.order_id, 'unsettle')} className="border-slate-700 text-slate-100 bg-transparent">
                  <Undo2 className="h-3 w-3 mr-1" />Annuler
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
